import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AssetClassLandAreaModel } from "src/app/core/asset_class";
import { AssetClassSizeModel, AssetClassTenure } from "src/app/core/comparable";
import { MadAuthService } from "src/app/core/mad-auth/mad-auth.service";
import { EsgIndicator } from "src/app/core/v2/state/esg-indicator/type";
import { SubdomainService } from "src/app/core/_base/subdomain.service";
import { environment } from "src/environments/environment";
import { AncillaryBuilding, SpecialPurposeAssetClass } from "./special-purpose";
import { v4 as uuidv4 } from 'uuid'

@Injectable({
  providedIn: 'root'
})
export class SpecialPurposeAssetClassAPIService {
  constructor(
    private http: HttpClient,
    private authService: MadAuthService,
    private subdomainService: SubdomainService
  ) { }

  fetchList(): Observable<{ data: SpecialPurposeAssetClass[] }> {
    return this.http.get<{ data: SpecialPurposeAssetClassAPIResource[] }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/special-purpose`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => {
        return {
          data: response.data.map(mapAPIResourceToModel)
        }
      })
    )
  }

  fetch(id: number): Observable<SpecialPurposeAssetClass> {
    return this.http.get<{ data: SpecialPurposeAssetClassAPIResource }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/special-purpose/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => mapAPIResourceToModel(response.data))
    )
  }

  store(assetClass: SpecialPurposeAssetClass, fileList: any[]) {
    return this.http.post<unknown>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/special-purpose`,
      {
        payload: assetClass,
        fileList
      },
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  update(assetClass: SpecialPurposeAssetClass, fileList: any[]) {
    return this.http.put<unknown>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/special-purpose/${assetClass.id}`,
      {
        payload: assetClass,
        fileList
      },
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }
}

interface SpecialPurposeAssetClassAPIResource {
  id: number
  ref_num: string
  status: number
  form_type: number

  country_id: number
  city_id: number
  zip_code: string,
  latitude: string
  longitude: string
  location_grade_id: number
  address: string
  location_surroundings: string

  add_info_on_land: boolean,
  land_parcel_name: string,
  coordinate_reference_system_id: number,
  boundary_type_id: number,
  land_area: AssetClassLandAreaModel,
  points: any[],
  land_area_components: any[],

  property_type_id: number
  completion_year: string,
  building_name: string
  property_grade_id: number
  above_floors: number
  below_floors: number
  handover_standard_id: number
  state_of_repair_id: number
  foundation_type_id: number,
  developer: string,
  anchor_tenant: string,
  has_ancillary_buildings: boolean,
  construction_desc: string,

  accommodation: any
  serviceAndInfraData: any
  externalAspectData: any
  internalAspectData: any
  grounds: any
  sizes: AssetClassSizeModel[]
  esgIndicator: EsgIndicator
  tenures: AssetClassTenure[],
  buildings: any[]

  ac_pictures: any[]
  picture: string

  size: string,
  used_times: number,
  can_be_deleted: boolean,
  userDeletedBy: string
  deletedBy: string
  createdBy: string
  lastUpdatedBy: string
  created_at: string
  updated_at: string
}
function mapAPIResourceToModel(resource: SpecialPurposeAssetClassAPIResource): SpecialPurposeAssetClass {
  const ac = new SpecialPurposeAssetClass();
  ac.id = resource.id
  ac.ref_num = resource.ref_num
  ac.formType = resource.form_type
  ac.status = resource.status

  ac.location_data = {
    country_id: resource.country_id,
    city_id: resource.city_id,
    address: resource.address,
    latitude: Number(resource.latitude),
    longitude: Number(resource.longitude),
    location_grade_id: resource.location_grade_id,
    location_surrounding: resource.location_surroundings,
    boundary_type_id: resource.boundary_type_id,
    city_of_location: null,
    time_offset: null,
    zip_code: resource.zip_code,
    community_id: null,
    distance_to_the_sea: null,
    elevation_from_sea: null
  }
  ac.add_info_on_land = resource.add_info_on_land
  ac.land_parcel_name = resource.land_parcel_name
  ac.coordinate_reference_system_id = resource.coordinate_reference_system_id
  ac.boundary_type_id = resource.boundary_type_id
  if (resource.land_area) {
    ac.landArea = resource.land_area
  }
  ac.points = resource.points,
    ac.landAreaComponents = resource.land_area_components

  ac.property_type_id = resource.property_type_id
  ac.completion_year = resource.completion_year
  ac.property_grade_id = resource.property_grade_id
  ac.building_name = resource.building_name
  ac.above_floors = resource.above_floors
  ac.below_floors = resource.below_floors
  ac.handover_standard_id = resource.handover_standard_id
  ac.state_of_repair_id = resource.state_of_repair_id
  ac.foundation_type_id = resource.foundation_type_id
  ac.developer = resource.developer
  ac.anchor_tenant = resource.anchor_tenant
  ac.has_ancillary_buildings = resource.has_ancillary_buildings
  ac.construction_desc = resource.construction_desc

  ac.accommodation = resource.accommodation
  ac.serviceAndInfraData = resource.serviceAndInfraData
  ac.externalAspectData = resource.externalAspectData
  ac.internalAspectData = resource.internalAspectData
  ac.esgIndicator = resource.esgIndicator
  ac.grounds = resource.grounds
  ac.sizes = resource.sizes
  ac.tenures = resource.tenures

  ac.buildings = resource.buildings.map(b => {
    const building = new AncillaryBuilding()
    building.id = b.id
    building.uuid = uuidv4()
    building.name = b.name
    building.completion_year = b.completion_year
    building.floors = b.total_floors
    building.construction_desc = b.construction_desc
    building.main_wall_id = b.main_wall_id
    building.external_facade_id = b.external
    building.roof_covering_id = b.roof_covering_id
    building.size = b.size
    building.unit_id = b.unit_id
    return building;
  })

  ac.size = resource.size
  ac.can_be_deleted = resource.can_be_deleted
  ac.userDeletedBy = resource.userDeletedBy
  ac.deletedBy = resource.deletedBy
  ac.createdBy = resource.createdBy
  ac.lastUpdatedBy = resource.lastUpdatedBy
  ac.created_at = resource.created_at
  ac.updated_at = resource.updated_at
  ac.used_times = resource.used_times

  ac.ac_pictures = resource.ac_pictures
  ac.picture = resource.picture
  return ac
}